import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApprenticeshipRegistrationMapType } from "@/types/apprenticeship/apprentice";
import { ApprenticeshipCompanyMapType, ApprenticeshipEnterpriseType } from "@/types/apprenticeship/enterprise";
import { Building2 } from "lucide-react";
import "mapbox-gl/dist/mapbox-gl.css";
import Map, { Layer, LayerProps, MapLayerMouseEvent, MapRef, Marker, Source } from "react-map-gl";
import { useUser } from "@/providers/user";
import { ApprenticeshipOfferAdMapType } from "@/types/apprenticeship/offer_ad";
import { cn } from "@/lib/utils";
import { MapFilters } from "./filters";
import { useSearchParams } from "react-router-dom";
import { ApprenticeshipProductType } from "@/types/apprenticeship/product";

export interface RegistrationMapFilters {
    registration?: {
        candidates_pending?: boolean;
        candidates_searching?: boolean;
        candidates_interview?: boolean;
        candidates_all?: boolean;
    } | null;
    company?: {
        offer_new?: boolean; // Offers without any interview
        offer_future_interview?: boolean; // Offers with upcomming interviews
        offer_past_interview?: boolean; // Offers with past interviews
    } | null;
    offer_ad?: {
        applications_new?: boolean;
        applications_processing?: boolean;
        applications_processed?: boolean;
    } | null;
}

export interface ClusteredMapProps extends React.HTMLAttributes<HTMLDivElement> {
    items: ApprenticeshipRegistrationMapType[];
    companies?: ApprenticeshipCompanyMapType[];
    offer_ads?: ApprenticeshipOfferAdMapType[];
    company?: ApprenticeshipEnterpriseType;
    products: ApprenticeshipProductType[];
    /** When false, non-admins only see candidates they own (CE/CAP). */
    showAllCandidates?: boolean;
    interractive?: boolean;
    onItemClick?: (item: ApprenticeshipRegistrationMapType) => void;
    onCompanyClick?: (company: ApprenticeshipCompanyMapType) => void;
}

export function RegistrationsMap({
    items,
    companies,
    products,
    offer_ads,
    company,
    showAllCandidates = false,
    interractive = false,
    onItemClick,
    onCompanyClick,
    className,
    ...props
}: ClusteredMapProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, has_any_group } = useUser();
    const is_admin = has_any_group(["app_apprenticeship_admin"]);
    const mapRef = useRef<MapRef>(null);
    const [tooltip, setTooltip] = useState<React.ReactNode | null>(null);
    const [cursor, setCursor] = useState<string>("auto");
    const [defaultProduct, setDefaultProduct] = useState<string | undefined>(undefined);
    const filters = useMemo<RegistrationMapFilters | null>(
        () => ({
            registration: {
                candidates_pending: true,
                candidates_searching: true,
                candidates_interview: true,
                candidates_all: showAllCandidates,
            },
        }),
        [showAllCandidates]
    );
    const onMouseEnter = useCallback((event: MapLayerMouseEvent) => {
        setCursor("pointer");
        const { features } = event;
        const hoverFeature = features && features[0];
        if (hoverFeature?.properties?.tooltip) {
            setTooltip(
                <div
                    style={{ top: event.point.y - 25, left: event.point.x + 10 }}
                    className="absolute z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
                >
                    {hoverFeature?.properties?.tooltip}
                </div>
            );
        }
    }, []);

    useEffect(() => {
        if (company?.product_short_name) {
            const p = products.find(prod => prod.short_name === company?.product_short_name);
            if (p) {
                setDefaultProduct(p.id.toString());
                searchParams.set("product__in", p.id.toString());
                setSearchParams(searchParams);
            }
        }
    }, []);

    const onMouseLeave = useCallback(() => {
        setCursor("auto");
        setTooltip(null);
    }, []);

    const onClick = useCallback((event: MapLayerMouseEvent) => {
        const { features } = event;
        const clickedFeature = features && features[0];
        if (onItemClick && clickedFeature?.properties?.type == "registration") {
            const clickedItems = items.filter(item => item.id.toString() == clickedFeature?.properties?.id);
            if (clickedItems.length > 0) {
                onItemClick(clickedItems[0]);
            }
        }
        if (companies) {
            if (onCompanyClick && clickedFeature?.properties?.type == "company") {
                const clickedCompany = companies.filter(company => company.id.toString() == clickedFeature?.properties?.id);
                if (clickedCompany.length > 0) {
                    onCompanyClick(clickedCompany[0]);
                }
            }
        }
    }, []);

    const clusterLayer: LayerProps = {
        id: "clusters",
        type: "circle",
        source: "items",
        filter: ["has", "point_count"],
        paint: {
            "circle-color": "#51bbd6",
            "circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40]
        }
    };

    const clusterCountLayer: LayerProps = {
        id: "cluster-count",
        type: "symbol",
        source: "items",
        filter: ["has", "point_count"],
        layout: {
            "text-field": "{point_count_abbreviated}",
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
            "text-size": 12
        }
    };

    const unclusteredPointLayer: LayerProps = {
        id: "unclustered-point",
        type: "circle",
        source: "items",
        filter: ["!", ["has", "point_count"]],
        paint: {
            "circle-color": ["case", ["==", ["get", "status"], "Submitted"], "black", ["!", ["get", "is_owner"]], "#a855f7", ["<", ["get", "priority"], 10], "#4ade80", "#38bdf8"],
            "circle-radius": 7,
            "circle-stroke-width": 2,
            "circle-stroke-color": ["get", "stroke_color"]
        }
    };

    const geodata = useMemo<GeoJSON.FeatureCollection | undefined>(() => {
        return {
            type: "FeatureCollection",
            features: items
                .filter(r => {
                    if (!is_admin && r.user_ce?.crm_user_id != user?.id && r.user_cap?.crm_user_id != user?.id && !filters?.registration?.candidates_all) {
                        // Hide other candidates by default
                        return false;
                    }
                    if (r.status == "Submitted") {
                        if (filters?.registration?.candidates_pending) {
                            return true;
                        }
                    } else if (r.future_interviews_count > 0) {
                        if (filters?.registration?.candidates_interview) {
                            return true;
                        }
                    } else if (filters?.registration?.candidates_searching) {
                        return true;
                    }
                    return !filters;
                })
                .map(r => {
                    return {
                        type: "Feature",
                        properties: {
                            id: r.id.toString(),
                            type: "registration",
                            name: `${r.first_name} ${r.last_name}`,
                            status: r.status,
                            priority: r.priority,
                            interviews_count: r.applications_count,
                            future_interviews_count: r.future_interviews_count,
                            is_owner: is_admin || r.user_ce?.crm_user_id == user?.id || r.user_cap?.crm_user_id == user?.id,
                            stroke_color: r.apprenticeship_type == "Apprentissage" ? "#fff" : "#000",
                            tooltip: `${r.first_name} ${r.last_name} (${r.apprenticeship_type})`
                        },
                        geometry: { type: "Point", coordinates: [r.longitude!, r.latitude!, 0.0] }
                    };
                })
        };
    }, [items, user, filters]);

    const companiesgeodata = useMemo<GeoJSON.FeatureCollection | undefined>(() => {
        return {
            type: "FeatureCollection",
            features: companies
                ? companies
                      .filter(c => {
                          if (filters?.company?.offer_new && c.interview_unqualified_count == 0) {
                              return true;
                          }
                          if (filters?.company?.offer_future_interview && c.incoming_interview_count > 0) {
                              return true;
                          }
                          if (filters?.company?.offer_past_interview && c.interview_unqualified_count > 0) {
                              return true;
                          }
                          return !filters;
                      })
                      .map(c => {
                          return {
                              type: "Feature",
                              properties: {
                                  id: c.id.toString(),
                                  type: "company",
                                  name: `${c.name}`,
                                  interviews_count: c.incoming_interview_count,
                                  interview_total_count: c.interview_total_count,
                                  is_owner: true,
                                  color: c.interview_unqualified_count === 0 ? "132,204,22" : c.incoming_interview_count > 0 ? "234,179,8" : "168,85,247"
                              },
                              geometry: { type: "Point", coordinates: [c.longitude!, c.latitude!, 0.0] }
                          };
                      })
                : []
        };
    }, [companies, user, filters]);

    const companiesunclusteredPointLayer: LayerProps = {
        id: "companies-unclustered-point",
        type: "symbol",
        source: "companies",
        filter: ["!", ["has", "point_count"]],
        layout: {
            "icon-image": ["concat", "square-rgb-", ["get", "color"]],
            "icon-allow-overlap": true
        }
    };

    const companiesunclusteredTextLayer: LayerProps = {
        id: "companies-unclustered-text",
        type: "symbol",
        source: "companies",
        minzoom: 12,
        filter: ["!", ["has", "point_count"]],
        layout: {
            "text-field": "{name}",
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
            "text-size": 12,
            "text-offset": [0, -1.2],
            "icon-image": ["concat", "square-rgb-", ["get", "color"]]
        }
    };

    const offer_ads_geodata = useMemo<GeoJSON.FeatureCollection | undefined>(() => {
        return {
            type: "FeatureCollection",
            features: offer_ads
                ? offer_ads
                      .filter(oa => {
                          if (filters?.offer_ad?.applications_new && oa.applications_new > 0) {
                              return true;
                          }
                          if (filters?.offer_ad?.applications_processing && oa.applications_processing > 0) {
                              return true;
                          }
                          if (filters?.offer_ad?.applications_processed && oa.applications_processed > 0) {
                              return true;
                          }
                          if (filters?.offer_ad && Object.values(filters.offer_ad).filter(v => !v).length == 0) {
                              if (oa.applications_new == 0 && oa.applications_processing == 0 && oa.applications_processed == 0) {
                                  // When all checkboxes are checked, also display offer with no candidate
                                  return true;
                              }
                          }
                          return !filters;
                      })
                      .map(oa => {
                          return {
                              type: "Feature",
                              properties: {
                                  type: "offer_ad",
                                  count: filters
                                      ? (filters?.offer_ad?.applications_new ? oa.applications_new : 0) +
                                        (filters?.offer_ad?.applications_processing ? oa.applications_processing : 0) +
                                        (filters?.offer_ad?.applications_processed ? oa.applications_processed : 0)
                                      : oa.applications_new + oa.applications_processing + oa.applications_processed,
                                  applications_new: oa.applications_new,
                                  applications_processing: oa.applications_processing,
                                  applications_processed: oa.applications_processed,
                                  color: oa.paid ? "168,85,247" : "59,7,100",
                                  tooltip: oa.paid ? "Candidatures payantes" : "Candidatures WeRecruit"
                              },
                              // Add an offset to paid offer ads
                              geometry: { type: "Point", coordinates: [oa.longitude! + (oa.paid ? 0.01 : 0), oa.latitude!, 0.0] }
                          };
                      })
                : []
        };
    }, [offer_ads, user, filters]);

    const offeradsunclusteredPointLayer: LayerProps = {
        id: "offerads-unclustered-point",
        type: "symbol",
        source: "offerads",
        filter: ["!", ["has", "point_count"]],
        layout: {
            "icon-image": ["concat", "square-rgb-", ["get", "color"]],
            "icon-allow-overlap": true
        }
    };

    const offeradsunclusteredTextLayer: LayerProps = {
        id: "offerads-unclustered-text",
        type: "symbol",
        source: "offerads",
        layout: {
            "text-field": "{count}",
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
            "text-size": 12,
            "icon-image": ["concat", "square-rgb-", ["get", "color"]],
            "icon-allow-overlap": true
        },
        paint: {
            "text-color": "white"
        }
    };

    const getInitialViewState = () => {
        if (company && company.longitude && company.latitude) {
            return {
                longitude: company.longitude,
                latitude: company.latitude,
                zoom: 11
            };
        } else {
            return {
                longitude: 3.612963,
                latitude: 46.876952,
                zoom: 5.47
            };
        }
    };

    const companyMarker = useMemo(() => {
        if (company && company.longitude && company.latitude) {
            return (
                <Marker longitude={company.longitude} latitude={company.latitude} anchor="center">
                    <div className="text-center">
                        <div className="flex justify-center rounded-full bg-white p-2">
                            <Building2 />
                        </div>
                    </div>
                </Marker>
            );
        }
        return null;
    }, [company]);

    return (
        <>
            <div className={cn("relative", className)} {...props}>
                {tooltip}
                {filters && (
                    <div className="absolute left-6 top-6" style={{ zIndex: 2 }}>
                        <MapFilters key={defaultProduct} defaultValue={defaultProduct} />
                    </div>
                )}
                <Map
                    ref={mapRef}
                    mapboxAccessToken="pk.eyJ1Ijoid2wtamVhbmJhcHRpc3RlY2FwbGFuIiwiYSI6ImNsdXd2cmZyajAwOG4yaHAxY2J3bnBud28ifQ.XJxHTOB4SWUiaeSzWLnPWg"
                    initialViewState={getInitialViewState()}
                    cursor={cursor}
                    style={{ width: "100%", height: "100%" }}
                    interactiveLayerIds={["unclustered-point", "companies-unclustered-point", "companies-unclustered-text", "offerads-unclustered-text"]}
                    mapStyle="mapbox://styles/wl-jeanbaptistecaplan/clux983ul003701oc91ntfrw3"
                    onClick={interractive ? onClick : undefined}
                    onMouseEnter={interractive ? onMouseEnter : undefined}
                    onMouseLeave={interractive ? onMouseLeave : undefined}
                    onLoad={() => {
                        mapRef?.current?.on("styleimagemissing", e => {
                            const id = e.id; // id of the missing image
                            // Check if this missing icon is
                            // one this function can generate.
                            const prefix = "square-rgb-";
                            if (!id.includes(prefix)) return;

                            // Get the color from the id.
                            const rgb = id.replace(prefix, "").split(",").map(Number);
                            const width = 16; // The image will be 64 pixels square.
                            const bytesPerPixel = 4; // Each pixel is represented by 4 bytes: red, green, blue, and alpha.
                            const data = new Uint8Array(width * width * bytesPerPixel);

                            for (let x = 0; x < width; x++) {
                                for (let y = 0; y < width; y++) {
                                    const offset = (y * width + x) * bytesPerPixel;
                                    data[offset + 0] = rgb[0]; // red
                                    data[offset + 1] = rgb[1]; // green
                                    data[offset + 2] = rgb[2]; // blue
                                    data[offset + 3] = 255; // alpha
                                }
                            }
                            mapRef?.current?.addImage(id, { width: width, height: width, data: data });
                        });
                    }}
                >
                    {geodata && (
                        <Source id="items" type="geojson" data={geodata} cluster={true} clusterMaxZoom={12} clusterRadius={50}>
                            <Layer {...clusterLayer} />
                            <Layer {...clusterCountLayer} />
                            <Layer {...unclusteredPointLayer} />
                        </Source>
                    )}
                    {companiesgeodata && (
                        <Source id="companies" type="geojson" data={companiesgeodata}>
                            {/* <Layer {...companiesclusterLayer} /> */}
                            {/* <Layer {...companiesclusterCountLayer} /> */}
                            <Layer {...companiesunclusteredPointLayer} />
                            <Layer {...companiesunclusteredTextLayer} />
                        </Source>
                    )}
                    {offer_ads_geodata && (
                        <Source id="offerads" type="geojson" data={offer_ads_geodata}>
                            {/* <Layer {...companiesclusterLayer} /> */}
                            {/* <Layer {...companiesclusterCountLayer} /> */}
                            <Layer {...offeradsunclusteredPointLayer} />
                            <Layer {...offeradsunclusteredTextLayer} />
                        </Source>
                    )}
                    {companyMarker && companyMarker}
                </Map>
            </div>
        </>
    );
}
